
interface ErrorContext {
  userId?: string;
  route?: string;
  action?: string;
  type?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  additionalData?: Record<string, unknown>;
}

class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;
  private isInitialized = false;

  static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  initialize() {
    if (this.isInitialized) return;

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      this.logError(event.reason, {
        type: 'unhandledRejection',
        route: window.location.pathname
      });
      
      // Prevent the default browser behavior
      event.preventDefault();
    });

    // Handle JavaScript errors
    window.addEventListener('error', (event) => {
      console.error('Global error:', event.error);
      this.logError(event.error, {
        type: 'javascriptError',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        route: window.location.pathname
      });
    });

    this.isInitialized = true;
  }

  logError(error: Error | string, context: ErrorContext = {}) {
    const errorData = {
      message: typeof error === 'string' ? error : error instanceof Error ? error.message : "An unknown error occurred",
      stack: typeof error === 'object' && error.stack ? error.stack : undefined,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      context
    };

    if (import.meta.env.PROD) {
      // Send to monitoring service
      this.sendToMonitoringService(errorData);
    } else {
      console.error('Error logged:', errorData);
    }
  }

  private sendToMonitoringService(errorData: Record<string, unknown>) {
    // This would integrate with your monitoring service
    try {
      // Example: Send to your backend or monitoring service
      fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData),
      }).catch(err => {
        console.error('Failed to send error to monitoring service:', err);
      });
    } catch (err) {
      console.error('Error sending to monitoring service:', err);
    }
  }

  // Utility method for manual error reporting
  reportError(error: Error | string, context?: ErrorContext) {
    this.logError(error, context);
  }
}

export const errorHandler = GlobalErrorHandler.getInstance();
export default errorHandler;
